import { useEffect, useState } from "react";
import { fetchNowSnapshot } from "@/lib/api";
import { Flame, Clock3, ArrowRight } from "lucide-react";

/**
 * "What's happening right now" strip that sits above the FilterBar.
 *
 * Shows one of three configurations driven by /api/now:
 *   • starts_soon_count > 0 → orange CTA "N classes starting in the next hour"
 *   • open_now_count > 0     → black CTA "N studios open right now"
 *   • else (after hours)     → "Next class window — H:MM AM · in 2h 13m · N studios"
 *
 * Clicking the CTA toggles the "starts_soon_only" filter on the parent
 * HomePage (via the onPickStartsSoon prop).
 *
 * Refreshes every 60s so the "in 2h 13m" counter doesn't go stale while the
 * user dwells on the page.
 */
export function NextWindowStrip({ onPickStartsSoon, onClearStartsSoon, startsSoonActive }) {
    const [snap, setSnap] = useState(null);

    useEffect(() => {
        let alive = true;
        const tick = () => fetchNowSnapshot()
            .then((s) => alive && setSnap(s))
            .catch(() => {});
        tick();
        const id = setInterval(tick, 60_000);
        return () => {
            alive = false;
            clearInterval(id);
        };
    }, []);

    if (!snap) return null;

    const {
        open_now_count: openNow = 0,
        starts_soon_count: startsSoon = 0,
        next_open_in_min: nextMin,
        next_open_label: nextLabel,
        next_open_count: nextCount,
        categories_next: cats = [],
        now_label: nowLabel,
    } = snap;

    const fmtMin = (m) => {
        if (m == null) return "";
        if (m < 60) return `${m}m`;
        const h = Math.floor(m / 60);
        const r = m % 60;
        return r ? `${h}h ${r}m` : `${h}h`;
    };

    // Pick the message + style.
    let variant; // 'live' | 'open' | 'preview'
    let primary; // big headline text
    let secondary; // small line under it
    let cta;       // CTA button content + behaviour

    if (startsSoon > 0) {
        variant = "live";
        primary = (
            <>
                <span className="font-display text-3xl md:text-4xl">{startsSoon}</span>
                <span className="font-editorial text-xl md:text-2xl"> classes</span>
                <span className="font-display text-3xl md:text-4xl"> starting now.</span>
            </>
        );
        secondary = `${nowLabel} · NYC time`;
        cta = {
            label: startsSoonActive ? "Showing live" : "Show me",
            onClick: startsSoonActive ? onClearStartsSoon : onPickStartsSoon,
            active: startsSoonActive,
        };
    } else if (openNow > 0) {
        variant = "open";
        primary = (
            <>
                <span className="font-display text-3xl md:text-4xl">{openNow}</span>
                <span className="font-editorial text-xl md:text-2xl"> studios</span>
                <span className="font-display text-3xl md:text-4xl"> open right now.</span>
            </>
        );
        secondary = `${nowLabel} · NYC time · next big window opens ${nextLabel || "later"}`;
        cta = nextMin != null && nextCount > 0
            ? {
                label: startsSoonActive
                    ? "Showing next cohort"
                    : `Show ${nextCount} opening at ${nextLabel}`,
                onClick: startsSoonActive ? onClearStartsSoon : onPickStartsSoon,
                active: startsSoonActive,
              }
            : null;
    } else if (nextMin != null) {
        variant = "preview";
        primary = (
            <>
                <span className="font-editorial text-xl md:text-2xl">Next class window —</span>{" "}
                <span className="font-display text-3xl md:text-4xl">{nextLabel}</span>
                <span className="font-editorial text-xl md:text-2xl"> · in {fmtMin(nextMin)}</span>
            </>
        );
        secondary =
            `${nextCount} ${nextCount === 1 ? "studio" : "studios"} opening` +
            (cats.length ? ` · ${cats.map((c) => c.name).join(", ")}` : "");
        cta = {
            label: startsSoonActive ? "Showing next cohort" : `Show me`,
            onClick: startsSoonActive ? onClearStartsSoon : onPickStartsSoon,
            active: startsSoonActive,
        };
    } else {
        // No upcoming windows today (very rare — only after the last studio
        // closes at midnight). Render nothing.
        return null;
    }

    return (
        <section
            className={`px-6 md:px-12 py-5 border-y border-foreground ${
                variant === "live" ? "bg-primary text-background" : "bg-foreground text-background"
            }`}
            data-testid="next-window-strip"
        >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                    <div
                        className={`mt-1 inline-flex h-9 w-9 items-center justify-center border ${
                            variant === "live"
                                ? "border-background bg-background text-primary"
                                : "border-background text-background"
                        }`}
                        aria-hidden
                    >
                        {variant === "live" ? <Flame className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
                    </div>
                    <div>
                        <div
                            className="font-mono-label text-[10px] uppercase tracking-widest mb-1 opacity-70"
                            data-testid="next-window-label"
                        >
                            {variant === "live"
                                ? "LIVE · Drop-in window"
                                : variant === "open"
                                ? "OPEN NOW"
                                : "NEXT WINDOW"}
                        </div>
                        <div className="leading-tight" data-testid="next-window-primary">
                            {primary}
                        </div>
                        <div className="font-mono-label text-[10px] uppercase tracking-widest mt-2 opacity-80">
                            {secondary}
                        </div>
                    </div>
                </div>

                {cta && cta.onClick && (
                    <button
                        onClick={cta.onClick}
                        className={`group inline-flex items-center gap-2 px-5 py-3 font-mono-label text-[11px] uppercase tracking-widest border transition-colors ${
                            cta.active
                                ? "bg-background text-foreground border-background"
                                : variant === "live"
                                ? "bg-background text-primary border-background hover:bg-foreground hover:text-background"
                                : "bg-background text-foreground border-background hover:bg-primary hover:text-background hover:border-primary"
                        }`}
                        data-testid="next-window-cta"
                    >
                        {cta.label}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </button>
                )}
                {cta && !cta.onClick && (
                    <div
                        className="inline-flex items-center gap-2 px-5 py-3 font-mono-label text-[11px] uppercase tracking-widest border border-background opacity-90"
                        data-testid="next-window-info"
                    >
                        {cta.label}
                    </div>
                )}
            </div>
        </section>
    );
}
